/**
 * @fileoverview ChatSheet - Premium slideout chat panel
 *
 * Clean, modern chat interface in a right-side sheet.
 * Branded header with Morgan logo. Supports multiple threads
 * with a history dropdown and "New chat" button.
 *
 * Pop-out mode: the header's pop-out button detaches the chat into a
 * floating window (fixed, draggable by its header, resizable via the
 * native CSS resize handle) that stays on top of everything — so the
 * practitioner can keep the conversation visible while working in the
 * app. Dock returns it to the sheet.
 */

import { useState, useMemo, useRef, useCallback } from "react";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Logo } from "@/ui/layout/logo";
import {
  Plus,
  History,
  MessageSquare,
  ChevronDown,
  Check,
  Maximize2,
  Minimize2,
  PictureInPicture2,
  PanelRight,
  X,
} from "lucide-react";
import { ExportButton } from "@/ui/composed/ExportButton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/ui/composed/overlays/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInput, type ChatMentionItem } from "./ChatInput";
import type { ChatMessageData, ChatUserInfo } from "./ChatMessage";

export interface ChatThreadInfo {
  threadId: string;
  createdAt: number;
  updatedAt: number;
  preview?: string;
}

interface ChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  messages: ChatMessageData[];
  onSend: (message: string) => void;
  userInfo?: ChatUserInfo;
  isLoading?: boolean;
  isSending?: boolean;
  /** Show a thinking indicator after the last message */
  isThinking?: boolean;
  /** Tool names currently being executed (shown in thinking indicator) */
  activeToolCalls?: string[];
  /** Live one-line status for long-running work (see ChatMessageList). */
  activityStatus?: string | null;
  emptyMessage?: string;
  inputPlaceholder?: string;
  /**
   * Optional text to pre-fill the input composer with. Used by the mail
   * "Draft with Morgan" flow to seed the chat from the email composer.
   */
  initialInput?: string;
  /**
   * Optional content rendered between the message list and the input
   * composer. Used by the precedent AI editor to show a sticky Review
   * card (Apply / Reject) above the prompt while a proposal is staged.
   */
  inputBanner?: React.ReactNode;
  threads?: ChatThreadInfo[];
  activeThreadId?: string | null;
  onNewThread?: () => void;
  onSelectThread?: (threadId: string) => void;
  onDocumentClick?: (documentId: string) => void;
  className?: string;
  /**
   * Tailwind width classes for the docked sheet in its normal (non-maximized)
   * state. Defaults to the wide `w-full sm:max-w-3xl`; pass a narrower value
   * (e.g. `w-full sm:max-w-[400px]`) for compact side-panel chats. Maximize
   * still expands to full width regardless of this.
   */
  widthClassName?: string;
  /** Show the Export button when there are messages (default true). */
  showExport?: boolean;
  /** Show the maximize / full-screen toggle (default true). */
  showMaximize?: boolean;
  /**
   * Items offered by the composer's `@`-mention dropdown (e.g. jobs, staff,
   * categories). Forwarded to `ChatInput`; omit to disable mentions.
   */
  mentionItems?: ChatMentionItem[];
}

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function ChatSheet({
  open,
  onOpenChange,
  title = "Chat",
  description,
  messages,
  onSend,
  userInfo,
  isLoading,
  isSending,
  isThinking,
  activeToolCalls,
  activityStatus,
  emptyMessage,
  inputPlaceholder,
  initialInput,
  inputBanner,
  threads = [],
  activeThreadId,
  onNewThread,
  onSelectThread,
  onDocumentClick,
  className,
  widthClassName = "w-full sm:max-w-3xl",
  showExport = true,
  showMaximize = true,
  mentionItems,
}: ChatSheetProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFloating, setIsFloating] = useState(false);
  const hasThreads = threads.length > 0;

  const exportContent = useMemo(() => {
    if (messages.length === 0) return null;
    return {
      title: title,
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
    };
  }, [messages, title]);

  if (open && isFloating) {
    return (
      <FloatingChatWindow
        title={title}
        description={description}
        onDock={() => setIsFloating(false)}
        onClose={() => {
          setIsFloating(false);
          onOpenChange(false);
        }}
      >
        <ChatMessageList
          messages={messages}
          userInfo={userInfo}
          onDocumentClick={onDocumentClick}
          isLoading={isLoading}
          isThinking={isThinking}
          activeToolCalls={activeToolCalls}
          activityStatus={activityStatus}
          emptyMessage={emptyMessage}
          className="min-h-0 flex-1 bg-muted/20"
        />
        {inputBanner}
        <ChatInput
          onSend={onSend}
          isDisabled={isSending}
          placeholder={inputPlaceholder}
          initialValue={initialInput}
          mentionItems={mentionItems}
        />
      </FloatingChatWindow>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideDefaultClose
        className={cn(
          "flex flex-col p-0",
          isMaximized ? "w-screen max-w-none sm:max-w-none" : widthClassName,
          className,
        )}
      >
        {/* Header */}
        <SheetHeader className="border-b bg-background/80 px-5 pb-4 pt-5 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary shadow-sm">
              <Logo
                compact
                width={18}
                height={18}
                className="text-primary-foreground"
              />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-semibold">
                {title}
              </SheetTitle>
              {description && (
                <SheetDescription className="mt-0.5 text-xs">
                  {description}
                </SheetDescription>
              )}
            </div>

            {/* Thread controls + export */}
            <div className="flex shrink-0 items-center gap-1.5">
              {showExport && exportContent && (
                <ExportButton
                  content={exportContent}
                  filename={`${title.replace(/\s+/g, "_")}_Chat`}
                  variant="ghost"
                  size="sm"
                />
              )}
              {hasThreads && (
                <DropdownMenu open={historyOpen} onOpenChange={setHistoryOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <History className="h-3.5 w-3.5" />
                      History
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 p-2">
                    <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Previous conversations
                    </p>
                    <div className="max-h-[320px] space-y-0.5 overflow-y-auto">
                      {threads.map((thread) => (
                        <button
                          key={thread.threadId}
                          type="button"
                          onClick={() => {
                            onSelectThread?.(thread.threadId);
                            setHistoryOpen(false);
                          }}
                          className={cn(
                            "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors",
                            thread.threadId === activeThreadId
                              ? "bg-accent/20 text-foreground"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-50" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium leading-tight">
                              {thread.preview
                                ? `"${thread.preview}${thread.preview.length >= 80 ? "…" : ""}"`
                                : "New conversation"}
                            </p>
                            <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                              {formatRelativeTime(thread.updatedAt)}
                            </p>
                          </div>
                          {thread.threadId === activeThreadId && (
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onNewThread}
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                title="Start new conversation"
              >
                <Plus className="h-3.5 w-3.5" />
                New
              </Button>
              {showMaximize && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMaximized((m) => !m)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                  title={
                    isMaximized ? "Exit full screen" : "Expand to full screen"
                  }
                  aria-label={
                    isMaximized ? "Exit full screen" : "Expand to full screen"
                  }
                >
                  {isMaximized ? (
                    <Minimize2 className="h-3.5 w-3.5" />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFloating(true)}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                title="Pop out into a floating window"
                aria-label="Pop out into a floating window"
              >
                <PictureInPicture2 className="h-3.5 w-3.5" />
              </Button>
              <SheetClose asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                  title="Close"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        {/* Messages */}
        <ChatMessageList
          messages={messages}
          userInfo={userInfo}
          onDocumentClick={onDocumentClick}
          isLoading={isLoading}
          isThinking={isThinking}
          activeToolCalls={activeToolCalls}
          activityStatus={activityStatus}
          emptyMessage={emptyMessage}
          className="min-h-0 flex-1 bg-muted/20"
        />

        {/* Optional banner directly above the input — used by the
            precedent AI editor for the Apply / Reject Review card. */}
        {inputBanner}

        {/* Input */}
        <ChatInput
          onSend={onSend}
          isDisabled={isSending}
          placeholder={inputPlaceholder}
          initialValue={initialInput}
          mentionItems={mentionItems}
        />
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Floating pop-out window
// ---------------------------------------------------------------------------

/**
 * Detached chat window: fixed-position above everything (z-[90], higher
 * than dialogs/sheets at z-50), draggable by its header, and resizable via
 * the native CSS `resize` handle in the bottom-right corner.
 */
function FloatingChatWindow(props: {
  title: string;
  description?: string;
  onDock: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { title, description, onDock, onClose, children } = props;
  const [position, setPosition] = useState(() => ({
    x: Math.max(16, window.innerWidth - 460),
    y: 88,
  }));
  const dragState = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
  } | null>(null);

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Don't start a drag from the header buttons.
      if ((e.target as HTMLElement).closest("button")) return;
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseX: position.x,
        baseY: position.y,
      };
      const onMove = (ev: PointerEvent) => {
        const drag = dragState.current;
        if (!drag) return;
        setPosition({
          x: Math.min(
            Math.max(8, drag.baseX + ev.clientX - drag.startX),
            window.innerWidth - 240,
          ),
          y: Math.min(
            Math.max(8, drag.baseY + ev.clientY - drag.startY),
            window.innerHeight - 80,
          ),
        });
      };
      const onUp = () => {
        dragState.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [position.x, position.y],
  );

  return (
    <div
      className="fixed z-[90] flex flex-col overflow-hidden rounded-xl border bg-background shadow-2xl"
      style={{
        left: position.x,
        top: position.y,
        width: 420,
        height: 560,
        minWidth: 320,
        minHeight: 360,
        maxWidth: "95vw",
        maxHeight: "92vh",
        resize: "both",
      }}
      role="dialog"
      aria-label={`${title} chat (floating)`}
    >
      <div
        className="flex shrink-0 cursor-move select-none items-center gap-2.5 border-b bg-background/95 px-4 py-3"
        onPointerDown={onHeaderPointerDown}
        title="Drag to move"
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary shadow-sm">
          <Logo
            compact
            width={14}
            height={14}
            className="text-primary-foreground"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-none">{title}</p>
          {description && (
            <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDock}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          title="Dock back to the side panel"
          aria-label="Dock back to the side panel"
        >
          <PanelRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          title="Close"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {children}
    </div>
  );
}
